import { useMemo } from 'react';
import { capitalizeFirstLetter, Option } from '@common-fe/common-fe';

import regexp from '@/common/regexp';
import { useDocumentOcrStore } from '@/modules/UseMyMoney/stores';
import { GroupServicesKey } from '@/modules/UseMyMoney/UseMyMoney.types';

const MAX_SERVICE_OPTIONS = 3;

interface Props {
  serviceId?: string;
  servicesGrouping: GroupServicesKey;
}

const useServiceOptions = ({ serviceId, servicesGrouping }: Props) => {
  const { ocrDocument } = useDocumentOcrStore();

  const suggestedServiceOptions = useMemo((): Option<string>[] => {
    if (!ocrDocument?.serviceOptions?.length) {
      return [];
    }

    const suggestedServices = ocrDocument?.serviceOptions?.filter((service) => !!service?.isSuggested && !!service?.serviceName);

    return suggestedServices
      ?.slice(0, MAX_SERVICE_OPTIONS)
      ?.map((suggestedService) => ({
        key: suggestedService?.serviceId,
        title: suggestedService?.serviceName,
        value: suggestedService?.serviceId,
      }));
  }, [ocrDocument?.serviceOptions]);

  const allServiceOptions = useMemo((): Option[] => {
    if (!ocrDocument?.serviceOptions?.length) {
      return [];
    }

    const services = ocrDocument?.serviceOptions?.filter((service) => !!service?.serviceName);

    return services?.map((service) => ({
      key: service?.serviceId,
      title: service?.serviceName,
      value: service?.serviceId,
      groupKey: service?.categoryType
        ? capitalizeFirstLetter(service?.categoryType).replace(regexp.DASH_SYMBOL, ' ')
        : '',
    }));
  }, [ocrDocument?.serviceOptions]);

    const accountOrdersServices = useMemo(() => {
    if (!ocrDocument?.accountOrdersArraysWithUniqServices?.length) {
      return [];
    }

    const groups = ocrDocument?.accountOrdersArraysWithUniqServices?.map((accountOrders) => {
      const groupKey = accountOrders?.[0]?.index;
      const services = accountOrders?.map((accountOrder) => accountOrder?.services)?.flat();
      return services?.map((service) => ({
        key: service?.id,
        title: service?.name,
        value: service?.id,
        groupKey: groupKey,
      }));
    });

    return groups?.flat();
  }, [ocrDocument?.accountOrdersArraysWithUniqServices]);

  const activeServiceOption = useMemo(() => allServiceOptions?.find((serviceOption) => serviceOption?.key === serviceId),
    [allServiceOptions, serviceId]);

  const serviceOptions = useMemo(() => {
    if (servicesGrouping === GroupServicesKey.CATEGORY) {
      return allServiceOptions;
    }

    if (servicesGrouping === GroupServicesKey.ACCOUNT) {
      return accountOrdersServices;
    }

    return [];
  }, [servicesGrouping, allServiceOptions, accountOrdersServices]);

  return {
    suggestedServiceOptions,
    activeServiceOption,
    serviceOptions,
  };
};

export default useServiceOptions;
